import React, { useCallback, useRef, useState } from "react";
import { construirTicket } from "../../lib/impresion";
import { SERVICIOS_CONOCIDOS, interpretarEstado } from "../../lib/impresion/blePrinter";

/**
 * Diagnóstico de impresoras Bluetooth. Herramienta de soporte, no pantalla de producto.
 *
 * Sirve para el día que un club aparezca con una impresora distinta: descubre sus
 * servicios y características sin saber nada de ella, prueba cada canal con una marca
 * distinta —así el PAPEL dice cuál es el bueno— y le pregunta el estado.
 *
 * Con esto se resolvió en veinte minutos la Jolimark TP510 de CRUO (27-ago-2026), que
 * a ciegas habrían sido horas. Sin sesión, como el resto de `/dev/*`: no toca la API ni
 * expone un solo dato del club, y el momento de usarla es un local con una impresora
 * muerta y alguien de soporte al teléfono — pedir credenciales ahí es el peor momento.
 *
 * Reutiliza el módulo de verdad (`lib/impresion`) a propósito: si el ticket sale bien
 * aquí, sale bien en la Barra.
 */

const TROZO = 20;
const PAUSA_MS = 20;

const corto = (uuid) => {
  const m = /^0000([0-9a-f]{4})-0000-1000-8000-00805f9b34fb$/i.exec(uuid);
  return m ? m[1].toUpperCase() : uuid.toUpperCase();
};

const COMANDA_EJEMPLO = {
  id: "diagnostico",
  fulfillment: "serve",
  table_label: "Mesa 4",
  customer_name: "Prueba de impresión",
  created_at: new Date().toISOString(),
  total: "12.50",
  items: [
    { id: "1", quantity: 2, product_name: "Cerveza", subtotal: "6.00" },
    { id: "2", quantity: 1, product_name: "Hamburguesa completa con extra de queso",
      notes: "sin cebolla y la carne muy hecha", subtotal: "6.50" },
  ],
};

export default function ImpresoraDevHarness() {
  const [lineas, setLineas] = useState([]);
  const [canales, setCanales] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [extra, setExtra] = useState("");
  const [conectando, setConectando] = useState(false);
  const dispositivoRef = useRef(null);

  const apunta = useCallback((texto, tono = "normal") => {
    const hora = new Date().toLocaleTimeString("es-ES");
    setLineas((previas) => [...previas.slice(-200), { hora, texto, tono }]);
  }, []);

  const escribir = useCallback(async (caracteristica, bytes) => {
    const conAcuse = caracteristica.properties.write && caracteristica.writeValue;
    for (let i = 0; i < bytes.length; i += TROZO) {
      const trozo = bytes.slice(i, i + TROZO);
      // eslint-disable-next-line no-await-in-loop -- troceado en serie: estas térmicas
      // pierden bytes si se les manda todo de golpe.
      if (conAcuse) await caracteristica.writeValue(trozo);
      else await caracteristica.writeValueWithoutResponse(trozo);
      // eslint-disable-next-line no-await-in-loop -- ver arriba
      await new Promise((r) => setTimeout(r, PAUSA_MS));
    }
  }, []);

  const conectar = useCallback(async () => {
    setConectando(true);
    setCanales([]);
    setElegido(null);
    try {
      const extras = extra.split("\n").map((t) => t.trim().toLowerCase()).filter((t) => t.length > 3);
      apunta(`Pidiendo dispositivo (${SERVICIOS_CONOCIDOS.length + extras.length} servicios declarados)…`);

      const dispositivo = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [...SERVICIOS_CONOCIDOS, ...extras],
      });
      dispositivoRef.current = dispositivo;
      apunta(`Elegido: ${dispositivo.name || "(sin nombre)"}  id=${dispositivo.id}`, "bien");

      const servidor = await dispositivo.gatt.connect();
      apunta("GATT conectado", "bien");

      const servicios = await servidor.getPrimaryServices();
      apunta(`Servicios visibles: ${servicios.length}`);

      const encontrados = [];
      for (const servicio of servicios) {
        apunta(`  servicio ${corto(servicio.uuid)}`);
        let caracteristicas = [];
        try {
          // eslint-disable-next-line no-await-in-loop -- recorrido de descubrimiento
          caracteristicas = await servicio.getCharacteristics();
        } catch {
          apunta("  (no se pudieron leer sus características)", "aviso");
          continue;
        }
        for (const c of caracteristicas) {
          const props = Object.entries(c.properties)
            .filter(([, activo]) => activo === true)
            .map(([nombre]) => nombre);
          apunta(`    car ${corto(c.uuid)}  [${props.join(", ") || "—"}]`);
          if (c.properties.notify) {
            c.addEventListener("characteristicvaluechanged", (ev) => {
              const datos = new Uint8Array(ev.target.value.buffer);
              const leido = datos.length ? interpretarEstado(datos[0]) : null;
              apunta(
                `<<< ${corto(c.uuid)} contesta: ${Array.from(datos).map((b) => b.toString(16).padStart(2, "0")).join(" ").toUpperCase()}` +
                (leido ? `  → ${leido.sinPapel ? "SIN PAPEL" : leido.tapaAbierta ? "TAPA ABIERTA" : "todo correcto"}` : ""),
                "bien"
              );
            });
            c.startNotifications().catch(() => apunta(`  no se pudo escuchar ${corto(c.uuid)}`, "aviso"));
          }
          if (c.properties.write || c.properties.writeWithoutResponse) {
            encontrados.push({ caracteristica: c, servicio: servicio.uuid, props });
          }
        }
      }

      setCanales(encontrados);
      if (encontrados.length === 0) {
        apunta("Ninguna característica acepta escritura. Si LightBlue enseña otro servicio, añádelo arriba.", "mal");
      } else {
        apunta(`${encontrados.length} canal(es) candidato(s). Prueba TODOS y mira qué sale por papel.`, "bien");
      }
    } catch (e) {
      apunta(`${e?.name || "Error"}: ${e?.message || "no se pudo conectar"}`, "mal");
      apunta("Comprueba Bluetooth y UBICACIÓN activados (Android la exige para escanear).", "aviso");
    } finally {
      setConectando(false);
    }
  }, [extra, apunta]);

  /** Una marca distinta por canal: el papel dice cuál es el bueno en una sola pasada. */
  const probarTodos = useCallback(async () => {
    apunta(`--- Probando ${canales.length} canales con marca distinta ---`, "bien");
    for (let i = 0; i < canales.length; i += 1) {
      const { caracteristica } = canales[i];
      const etiqueta = `CANAL ${i + 1} = ${corto(caracteristica.uuid)}`;
      const bytes = new Uint8Array([0x1b, 0x40, ...[...etiqueta].map((c) => c.charCodeAt(0)), 0x0a, 0x0a, 0x0a, 0x0a]);
      try {
        // eslint-disable-next-line no-await-in-loop -- uno detrás de otro a propósito
        await escribir(caracteristica, bytes);
        apunta(`  canal ${i + 1} (${corto(caracteristica.uuid)}) enviado`);
      } catch (e) {
        apunta(`  canal ${i + 1} FALLÓ: ${e?.name}`, "mal");
      }
      // eslint-disable-next-line no-await-in-loop -- respiro entre trabajos
      await new Promise((r) => setTimeout(r, 1200));
    }
    apunta("--- Mira el papel: la LÍNEA impresa dice el canal bueno ---", "bien");
  }, [canales, escribir, apunta]);

  const preguntarEstado = useCallback(async () => {
    if (!elegido) return apunta("Elige antes un canal", "aviso");
    await escribir(elegido.caracteristica, new Uint8Array([0x10, 0x04, 0x01]));
    apunta(">>> estado pedido; si no contesta nada, ese canal no llega al motor de impresión", "aviso");
  }, [elegido, escribir, apunta]);

  const imprimirTicket = useCallback(async () => {
    if (!elegido) return apunta("Elige antes un canal", "aviso");
    try {
      await escribir(elegido.caracteristica, construirTicket(COMANDA_EJEMPLO));
      apunta("Ticket del módulo real enviado. ¿Ha salido papel?", "bien");
    } catch (e) {
      apunta(`No se pudo imprimir: ${e?.name}`, "mal");
    }
  }, [elegido, escribir, apunta]);

  const color = { bien: "text-emerald-400", mal: "text-rose-400", aviso: "text-amber-400", normal: "text-gray-300" };

  if (typeof navigator === "undefined" || !navigator.bluetooth) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <h1 className="text-xl font-bold text-gray-900">Diagnóstico de impresora</h1>
        <p className="mt-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          Este navegador no tiene Web Bluetooth. Ábrelo en <b>Chrome</b> (Android o escritorio),
          o en <b>Bluefy</b> si es un iPad — Safari no puede hablar con la impresora.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Diagnóstico de impresora</h1>
        <p className="text-sm text-gray-500">
          Descubrir servicio y canal de una impresora Bluetooth, y probar que imprime.
        </p>
      </div>

      <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4">
        <button
          type="button"
          onClick={conectar}
          disabled={conectando}
          className="rounded-lg bg-gray-900 px-4 py-3 font-semibold text-white disabled:opacity-40"
        >
          {conectando ? "Buscando…" : "Buscar impresora"}
        </button>
        <label className="mt-1 text-xs uppercase tracking-wide text-gray-400" htmlFor="extra">
          Servicios extra a sondear (uno por línea)
        </label>
        <textarea
          id="extra"
          rows={2}
          value={extra}
          onChange={(e) => setExtra(e.target.value)}
          placeholder="pega aquí un UUID si lo sacaste de LightBlue"
          className="rounded-lg border border-gray-300 p-2 font-mono text-xs"
        />
        <p className="text-xs text-gray-400">
          Se sondean {SERVICIOS_CONOCIDOS.length} servicios habituales. Si el de tu impresora no
          está, añádelo aquí y vuelve a buscar.
        </p>
      </div>

      {canales.length > 0 && (
        <div className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Canales que aceptan escritura
          </h2>
          {canales.map((c, i) => (
            <button
              key={c.caracteristica.uuid}
              type="button"
              onClick={() => { setElegido(c); apunta(`Canal elegido: ${corto(c.caracteristica.uuid)}`, "bien"); }}
              className={`rounded-lg border p-2 text-left text-xs ${
                elegido?.caracteristica.uuid === c.caracteristica.uuid
                  ? "border-emerald-400 bg-emerald-50"
                  : "border-gray-200"
              }`}
            >
              <span className="font-mono">{i + 1} · srv {corto(c.servicio)} / car {corto(c.caracteristica.uuid)}</span>
              <span className="block text-gray-500">{c.props.join(" · ")}</span>
            </button>
          ))}
          <div className="mt-1 flex flex-wrap gap-2">
            <button type="button" onClick={probarTodos} className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white">
              Probar TODOS los canales
            </button>
            <button type="button" onClick={preguntarEstado} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
              Preguntar estado
            </button>
            <button type="button" onClick={imprimirTicket} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
              Imprimir ticket real
            </button>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-800 bg-gray-900 p-3">
        <pre className="max-h-96 overflow-y-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
          {lineas.map((l, i) => (
            <span key={`${l.hora}-${i}`} className={color[l.tono]}>{l.hora}  {l.texto}{"\n"}</span>
          ))}
        </pre>
      </div>
    </div>
  );
}
